import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { paymentsAPI } from '../lib/api';
import { CheckCircle, Loader2, Calendar, ArrowRight } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setLoading(false);
        return;
      }

      try {
        const response = await paymentsAPI.getStatus(sessionId);
        setPaymentStatus(response.data);

        if (response.data.payment_status === 'paid') {
          setLoading(false);
          return;
        }

        // Continue polling if not yet paid
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts >= maxAttempts - 1) {
          setLoading(false);
        } else {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        }
      }
    };

    if (sessionId) {
      pollPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-20" data-testid="payment-success-page">
        <Card className="p-8 md:p-12 rounded-2xl text-center">
          {loading ? (
            <>
              <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-6" />
              <h1 className="font-heading text-2xl font-semibold mb-2">
                Processing Payment...
              </h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment.
              </p>
            </>
          ) : paymentStatus?.payment_status === 'paid' ? (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-foreground mb-3">
                Payment Successful!
              </h1>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                Your tour has been booked successfully. You will receive a confirmation email shortly.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/bookings">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8" data-testid="view-bookings-btn">
                    <Calendar className="h-4 w-4 mr-2" />
                    View My Bookings
                  </Button>
                </Link>
                <Link to="/tours">
                  <Button variant="outline" className="rounded-full px-8" data-testid="explore-more-btn">
                    Explore More Tours
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-12 w-12 text-yellow-600" />
              </div>
              <h1 className="font-heading text-2xl font-semibold mb-2">
                Payment Pending
              </h1>
              <p className="text-muted-foreground mb-6">
                Your payment is being processed. Check your bookings for status updates.
              </p>
              <Link to="/bookings">
                <Button className="rounded-full">View My Bookings</Button>
              </Link>
            </>
          )}
        </Card>
      </div>

      <Footer />
    </div>
  );
}
